import { ChevronDownIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FieldErrorMessage, LightText2 } from "../ui/Text";
import useClickOutside from "@/hooks/useClickOutSide";
import { useFloating, flip, shift, offset, autoUpdate } from "@floating-ui/react-dom";
import { Center } from "../css";

interface Option {
  label: string;
  value: string | number;
}

interface DependentSelectBoxProps {
  label: string;
  field: string;
  placeholder: string;
  options: {
    [key: string]: Option[];
  };
  optionKeyField: string;
  disabled?: boolean;
  validation?: {
    required?: boolean;
  };
  onChange?: (value: string | number) => void;
  showSearch?: boolean;
  defaultValue?: string;
  messageOnNoOption?: string;
}

const DependentSelectBox: React.FC<DependentSelectBoxProps> = ({
  field,
  placeholder,
  options,
  optionKeyField,
  disabled = false,
  validation,
  onChange,
  showSearch = false,
  defaultValue,
  messageOnNoOption = "Not applicable",
}) => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useClickOutside(() => setIsOpen(false));
  const errorMessage: any = errors[field]?.message;
  const selectedValue = watch(field);
  const dependentFieldValue = watch(optionKeyField);

  const { refs, floatingStyles }: any = useFloating({
    open: isOpen,
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const currentOptions = React.useMemo(() => {
    return options[dependentFieldValue] || [];
  }, [options, dependentFieldValue]);

  const isFieldDisabled = disabled || !dependentFieldValue;

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return currentOptions;
    const searchTermLower = searchTerm.toLowerCase();

    const categorizeOption = (option: any) => {
      const label = option.label.toLowerCase();

      // Exact matches
      if (label === searchTermLower) return "exactMatch";
      // Starts with matches
      if (label.startsWith(searchTermLower)) return "startsWith";
      // Contains matches
      if (label.includes(searchTermLower)) return "contains";
      return null;
    };
    const categorizedResults: any = {
      selected: [],
      exactMatch: [],
      startsWith: [],
      contains: [],
    };

    // First, handle selected value if exists
    if (selectedValue) {
      const selectedOption = currentOptions.find((option) => option.value === selectedValue);
      if (selectedOption) {
        categorizedResults.selected.push(selectedOption);
      }
    }

    // Categorize all matching options
    for (const option of currentOptions) {
      // Skip if it's the selected option (already handled)
      if (option.value === selectedValue) continue;

      const category = categorizeOption(option);
      if (category) {
        categorizedResults[category].push(option);
      }
    }
    return [...categorizedResults.selected, ...categorizedResults.exactMatch, ...categorizedResults.startsWith, ...categorizedResults.contains];
  }, [currentOptions, searchTerm, selectedValue]);

  const handleSelect = (option: Option) => {
    setValue(field, option.value, { shouldValidate: true });
    setIsOpen(false);
    setSearchTerm("");
    if (onChange) {
      onChange(option.value);
    }
  };

  const getSelectedLabel = () => {
    if (currentOptions.length === 0) return messageOnNoOption;
    const selected = currentOptions.find((option) => option.value === selectedValue);
    return selected ? selected.label : placeholder;
  };

  useEffect(() => {
    if (defaultValue) {
      setValue(field, defaultValue, { shouldDirty: false });
      trigger(field);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (inputRef.current && isOpen) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        type="hidden"
        {...register(field, {
          required: validation?.required ? "This field is required." : false,
        })}
      />

      <div
        className={`w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 cursor-pointer flex justify-between items-center ${
          errors[field]?.message ? "border-text-error" : ""
        } ${isFieldDisabled || currentOptions.length === 0 ? "!cursor-not-allowed opacity-60" : ""}`}
        onClick={() => !isFieldDisabled && currentOptions.length !== 0 && setIsOpen(!isOpen)}
        ref={refs.setReference}
      >
        {showSearch ? (
          <input
            type="text"
            ref={inputRef}
            value={searchTerm}
            onChange={(e) => {
              let value = e.target.value;
              if (value?.trim().length === 0) {
                value = value.trim();
              }
              setSearchTerm(value);
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={isFieldDisabled || currentOptions.length === 0}
            onFocus={() => setIsOpen(true)}
            placeholder={isOpen ? "Search..." : getSelectedLabel()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            className={`transition-all w-full duration-100 ease-in-out text-nowrap text-13 outline-none placeholder:text-gray-400 text-text-default`}
          />
        ) : (
          <span>{getSelectedLabel()}</span>
        )}
        <ChevronDownIcon className={`w-5 h-5 transition-transform ${isOpen ? "transform rotate-180" : ""}`} />
      </div>

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            width: refs.reference.current?.offsetWidth,
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`px-4 py-2 cursor-pointer hover:bg-primaryOpacity-10 font-normal text-13 text-text-default ${
                  selectedValue === option.value ? "bg-primaryOpacity-10 text-text-primary font-medium" : ""
                }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <Center className="px-4 py-6">
              <LightText2 text="No options found" />
            </Center>
          )}
        </div>
      )}

      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default DependentSelectBox;
